import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Loader2, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { AuthShell, Field } from "@/pages/Login";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Forgot password" subtitle="We'll email you a link to set a new one">
      {sent ? (
        <div className="text-center py-4" data-testid="forgot-sent">
          <MailCheck className="h-10 w-10 text-emerald-500 mx-auto mb-3" />
          <p className="font-semibold">Check your inbox</p>
          <p className="text-sm text-zinc-500 mt-1">
            If <b>{email}</b> is registered, a reset link is on its way. It expires in 1 hour.
            Don't forget to check your spam folder.
          </p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
          <Field label="Email" type="email" value={email} onChange={setEmail} testid="forgot-email" placeholder="you@example.com" />
          <button
            type="submit"
            disabled={loading}
            data-testid="forgot-submit"
            className="w-full h-12 rounded-xl bg-primary text-white font-semibold hover:bg-[#1E40AF] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} Send reset link
          </button>
        </form>
      )}
      <p className="text-sm text-zinc-500 text-center mt-6">
        Remembered it?{" "}
        <Link to="/login" className="text-primary font-semibold" data-testid="back-to-login">
          Back to login
        </Link>
      </p>
    </AuthShell>
  );
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) return setError("Password must be at least 6 characters");
    if (password !== confirm) return setError("Passwords don't match");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated! Log in with your new password.");
      navigate("/login");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!token)
    return (
      <AuthShell title="Invalid link" subtitle="This reset link is missing its code">
        <p className="text-sm text-zinc-500 text-center" data-testid="reset-no-token">
          Please use the link from your email, or{" "}
          <Link to="/forgot-password" className="text-primary font-semibold">request a new one</Link>.
        </p>
      </AuthShell>
    );

  return (
    <AuthShell title="Set a new password" subtitle="Choose a strong password you'll remember">
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        <Field label="New password" type="password" value={password} onChange={setPassword} testid="reset-password" placeholder="••••••••" />
        <Field label="Confirm new password" type="password" value={confirm} onChange={setConfirm} testid="reset-confirm" placeholder="••••••••" />
        {error && <p className="text-sm text-destructive font-medium" data-testid="reset-error">{error}</p>}
        <button
          type="submit"
          disabled={loading}
          data-testid="reset-submit"
          className="w-full h-12 rounded-xl bg-primary text-white font-semibold hover:bg-[#1E40AF] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {loading && <Loader2 className="h-4 w-4 animate-spin" />} Update password
        </button>
      </form>
    </AuthShell>
  );
}
